import { PageSizeEnum } from '../../../api/shared/enums/page-size.enum';
import { SortFieldEnum } from '../../../Common/TargetingShared/enum/SortField.enum';
import {
    UserAppBaseDisplaySettingsModel,
    UserAppTargetingDisplaySettingsModel,
    UserAppMonitoringDisplaySettingsModel,
    UserAppSavedCoverageDisplaySettingsModel,
    UserAppBriefingDisplaySettingsModel,
    SavedCoverageSortType,
    UserAppSubscriptionDisplaySettingsModel
} from '../../../Core/Users/models/user-app-default-display-preferences.model';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { MonitoringSortTypesEnum } from '../../../Common/MonitorShared/enum/monitoring-sort-types.enum';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { MonitoringViewModesEnum } from '@Monitor/Shared/enums/monitoring-view-modes.enum';
import { userAppPreferencesDefault } from '@Core/Users/data/user-app-preferences-default';

export type DisplaySettingsSelectionLabel = 'pageSize' | 'direction' | 'type';

export abstract class AbstractDisplaySettingsModel {
    protected readonly sortDirectionMap = {
        [SortOrderEnum.ascending]: new DropdownOptionModel('AccountPreferences.defaultResults.sortAsc', SortOrderEnum.ascending),
        [SortOrderEnum.descending]: new DropdownOptionModel('AccountPreferences.defaultResults.sortDesc', SortOrderEnum.descending),
    };


    public pageSizeOptions: DropdownOptionModel<PageSizeEnum>[] = [];
    public sortDirectionOptions: DropdownOptionModel<SortOrderEnum>[] = [];
    public sortTypeOptions: DropdownOptionModel<MonitoringSortTypesEnum>[] | DropdownOptionModel<SortFieldEnum>[] = [];
    public selectedPageSize: DropdownOptionModel<PageSizeEnum> = null;
    public selectedSortType: DropdownOptionModel<MonitoringSortTypesEnum> | DropdownOptionModel<SortFieldEnum> = null;
    public selectedSortDirection: DropdownOptionModel<SortOrderEnum> = null;

    public abstract handleSelectionChange(selection: DropdownOptionModel, type: DisplaySettingsSelectionLabel): void;
    public abstract getCurrentSelections(): UserAppBaseDisplaySettingsModel;
}

export class DiscoverDisplaySettingsModel extends AbstractDisplaySettingsModel {
    protected readonly pageSizesMap = {
        [PageSizeEnum.twentyFive]: new DropdownOptionModel('25', PageSizeEnum.twentyFive),
        [PageSizeEnum.fifty]: new DropdownOptionModel('50', PageSizeEnum.fifty),
        [PageSizeEnum.oneHundred]: new DropdownOptionModel('100', PageSizeEnum.oneHundred),
        [PageSizeEnum.twoFifty]: new DropdownOptionModel('250', PageSizeEnum.twoFifty)
    };

    protected readonly sortTypeMap = {
        [SortFieldEnum.CIRCULATION_UNIQUE_VISITORS]: new DropdownOptionModel(
            'Discover.userPreferences.sortFields.uniqueVisitors',
            SortFieldEnum.CIRCULATION_UNIQUE_VISITORS
        ),
        [SortFieldEnum.CIRCULATION_CIRCULATION]: new DropdownOptionModel(
            'Discover.userPreferences.sortFields.circulation',
            SortFieldEnum.CIRCULATION_CIRCULATION
        ),
        [SortFieldEnum.CIRCULATION_VIEWERS]: new DropdownOptionModel(
            'Discover.userPreferences.sortFields.viewers',
            SortFieldEnum.CIRCULATION_VIEWERS
        ),
        [SortFieldEnum.CIRCULATION_LISTENERS]: new DropdownOptionModel(
            'Discover.userPreferences.sortFields.listeners',
            SortFieldEnum.CIRCULATION_LISTENERS
        ),
        [SortFieldEnum.CIRCULATION_FOLLOWERS]: new DropdownOptionModel(
            'Discover.userPreferences.sortFields.followers',
            SortFieldEnum.CIRCULATION_FOLLOWERS
        ),
        [SortFieldEnum.FIRST_NAME]: new DropdownOptionModel(
            'Discover.userPreferences.sortFields.firstName',
            SortFieldEnum.FIRST_NAME
        ),
        [SortFieldEnum.LAST_NAME]: new DropdownOptionModel(
            'Discover.userPreferences.sortFields.lastName',
            SortFieldEnum.LAST_NAME
        ),
        [SortFieldEnum.JOB_TITLE]: new DropdownOptionModel(
            'Discover.userPreferences.sortFields.jobTitle',
            SortFieldEnum.JOB_TITLE
        ),
        [SortFieldEnum.OUTLET_NAME]: new DropdownOptionModel(
            'Discover.userPreferences.sortFields.outletName',
            SortFieldEnum.OUTLET_NAME
        ),
        [SortFieldEnum.COUNTRY_NAME]: new DropdownOptionModel(
            'Discover.userPreferences.sortFields.countryName',
            SortFieldEnum.COUNTRY_NAME
        ),
        [SortFieldEnum.PROVINCE_NAME]: new DropdownOptionModel(
            'Discover.userPreferences.sortFields.provinceName',
            SortFieldEnum.PROVINCE_NAME
        ),
        [SortFieldEnum.CITY_NAME]: new DropdownOptionModel(
            'Discover.userPreferences.sortFields.cityName',
            SortFieldEnum.CITY_NAME
        ),
    };

    public selectedSortType: DropdownOptionModel<SortFieldEnum> = null;

    constructor(defaultDisplayPreferences: UserAppTargetingDisplaySettingsModel) {
        super();
        this.selectedPageSize = this.pageSizesMap[defaultDisplayPreferences.pageSize];
        this.selectedSortType = this.sortTypeMap[defaultDisplayPreferences.sortType];
        this.selectedSortDirection = this.sortDirectionMap[defaultDisplayPreferences.sortDirection];

        this.pageSizeOptions = Object.values(this.pageSizesMap);
        this.sortTypeOptions = Object.values(this.sortTypeMap);
        this.sortDirectionOptions = Object.values(this.sortDirectionMap);
    }

    public handleSelectionChange(selection: DropdownOptionModel, type: DisplaySettingsSelectionLabel): void {
        switch (type) {
            case 'direction':
                this.selectedSortDirection = selection as DropdownOptionModel<SortOrderEnum>;
                break;
            case 'pageSize':
                this.selectedPageSize = selection as DropdownOptionModel<PageSizeEnum>;
                break;
            case 'type':
                this.selectedSortType = selection as DropdownOptionModel<SortFieldEnum>;
                break;
            default:
                throw new Error('invalid selection type');
        }
    }

    public getCurrentSelections(): UserAppTargetingDisplaySettingsModel {
        return {
            pageSize: this.selectedPageSize.value,
            sortDirection: this.selectedSortDirection.value,
            sortType: this.selectedSortType.value
        };
    }
}

export class MonitoringDisplaySettingsModel extends AbstractDisplaySettingsModel {
    // Puposefully missing .asc or .desc, these are added on after depending on
    // sortDirection choice
    protected readonly publishedLabel = 'Mentions.userPreferences.sortOrder.published';
    protected readonly availableLabel = 'Mentions.userPreferences.sortOrder.available';
    protected readonly audienceLabel = 'Mentions.userPreferences.sortOrder.audienceReach';
    protected readonly relevancyLabel = 'Mentions.userPreferences.sortOrder.relevancy';
    protected readonly domainAuthLabel = 'Mentions.userPreferences.sortOrder.domainAuthority';
    protected readonly engagementLabel = 'Mentions.userPreferences.sortOrder.engagement';
    protected readonly engagementRateLabel = 'Mentions.userPreferences.sortOrder.engagementRate';

    protected readonly pageSizesMap = {
        [PageSizeEnum.twentyFive]: new DropdownOptionModel('25', PageSizeEnum.twentyFive),
        [PageSizeEnum.fifty]: new DropdownOptionModel('50', PageSizeEnum.fifty),
        [PageSizeEnum.oneHundred]: new DropdownOptionModel('100', PageSizeEnum.oneHundred),
        [PageSizeEnum.twoHundred]: new DropdownOptionModel('200', PageSizeEnum.twoHundred)
    };

    protected sortTypeMap = {
        [MonitoringSortTypesEnum.published]: new DropdownOptionModel(
            `${this.publishedLabel}.asc`,
            MonitoringSortTypesEnum.published
        ),
        [MonitoringSortTypesEnum.available]: new DropdownOptionModel(
            `${this.availableLabel}.asc`,
            MonitoringSortTypesEnum.available
        ),
        [MonitoringSortTypesEnum.audience]: new DropdownOptionModel(
            `${this.audienceLabel}.asc`,
            MonitoringSortTypesEnum.audience
        ),
        [MonitoringSortTypesEnum.relevancy]: new DropdownOptionModel(
            `${this.relevancyLabel}.asc`,
            MonitoringSortTypesEnum.relevancy
        ),
        [MonitoringSortTypesEnum.domainAuthority]: new DropdownOptionModel(
            `${this.domainAuthLabel}.asc`,
            MonitoringSortTypesEnum.domainAuthority
        ),
    };

    protected engagementsSortTypeMap = {
        [MonitoringSortTypesEnum.engagement]: new DropdownOptionModel(
            `${this.engagementLabel}.asc`,
            MonitoringSortTypesEnum.engagement
        ),
        [MonitoringSortTypesEnum.engagement_rate]: new DropdownOptionModel(
            `${this.engagementRateLabel}.asc`,
            MonitoringSortTypesEnum.engagement_rate
        )
    };

    public defaultSortType: MonitoringSortTypesEnum;
    public selectedSortType: DropdownOptionModel<MonitoringSortTypesEnum> = null;

    public showImages: boolean;
    public showMentionIndex: boolean;
    public isCompactMentionDisplay: boolean;
    public resultsViewMode: MonitoringViewModesEnum;

    constructor(defaultDisplayPreferences: UserAppMonitoringDisplaySettingsModel) {
        super();
        this.selectedPageSize = this.pageSizesMap[defaultDisplayPreferences.pageSize];

        this.selectedSortDirection = this.sortDirectionMap[defaultDisplayPreferences.sortDirection];
        this.setSortLabelsBasedOnSortDirection(this.selectedSortDirection.value);

        this.defaultSortType = defaultDisplayPreferences.sortType;
        this.selectedSortType = this.sortTypeMap[defaultDisplayPreferences.sortType];

        this.pageSizeOptions = Object.values(this.pageSizesMap);
        this.sortTypeOptions = Object.values(this.sortTypeMap);
        this.sortDirectionOptions = Object.values(this.sortDirectionMap);

        this.showImages = defaultDisplayPreferences.showImages;
        this.showMentionIndex = defaultDisplayPreferences.showMentionIndex;
        this.isCompactMentionDisplay = defaultDisplayPreferences.isCompactMentionDisplay;
        if (defaultDisplayPreferences.resultsViewMode) {
            this.resultsViewMode = defaultDisplayPreferences.resultsViewMode;
        } else {
            this.resultsViewMode = userAppPreferencesDefault.monitoringPrefs.topicsPrefs.defaultDisplaySettings.resultsViewMode;
        }
    }

    public updateSortTypeOptions(hasSocialIntelligencePermission: boolean) {
        if (!hasSocialIntelligencePermission) {
            return;
        }

        const directionLabel = this.sortTypeOptions?.[0]?.label?.endsWith('desc') ? 'desc' : 'asc';

        this.sortTypeMap = {
            ...this.sortTypeMap,
            ...this.engagementsSortTypeMap
        };

        this.sortTypeOptions = Object.values(this.sortTypeMap);

        this.sortTypeMap[MonitoringSortTypesEnum.engagement].label = `${this.engagementLabel}.${directionLabel}`;
        this.sortTypeMap[MonitoringSortTypesEnum.engagement_rate].label = `${this.engagementRateLabel}.${directionLabel}`;

        if (!this.selectedSortType && this.defaultSortType) {
            this.selectedSortType = this.sortTypeMap[this.defaultSortType];
        }
    }

    public handleSelectionChange(selection: DropdownOptionModel, type: DisplaySettingsSelectionLabel): void {
        switch (type) {
            case 'direction':
                this.selectedSortDirection = selection as DropdownOptionModel<SortOrderEnum>;
                this.setSortLabelsBasedOnSortDirection(this.selectedSortDirection.value);
                break;
            case 'pageSize':
                this.selectedPageSize = selection as DropdownOptionModel<PageSizeEnum>;
                break;
            case 'type':
                this.selectedSortType = selection as DropdownOptionModel<MonitoringSortTypesEnum>;
                break;
            default:
                throw new Error('invalid selection type');
        }
    }

    public getCurrentSelections(): UserAppMonitoringDisplaySettingsModel {
        return {
            pageSize: this.selectedPageSize.value,
            sortDirection: this.selectedSortDirection.value,
            sortType: this.selectedSortType.value,
            showImages: this.showImages,
            showMentionIndex: this.showMentionIndex,
            isCompactMentionDisplay: this.isCompactMentionDisplay,
            resultsViewMode: this.resultsViewMode
        };
    }

    protected setSortLabelsBasedOnSortDirection(sortDirection: SortOrderEnum) {
        const directionLabel = sortDirection === SortOrderEnum.ascending
            ? 'asc'
            : 'desc';

        this.sortTypeMap[MonitoringSortTypesEnum.published].label = `${this.publishedLabel}.${directionLabel}`;
        this.sortTypeMap[MonitoringSortTypesEnum.audience].label = `${this.audienceLabel}.${directionLabel}`;
        this.sortTypeMap[MonitoringSortTypesEnum.available].label = `${this.availableLabel}.${directionLabel}`;
        this.sortTypeMap[MonitoringSortTypesEnum.relevancy].label = `${this.relevancyLabel}.${directionLabel}`;
        this.sortTypeMap[MonitoringSortTypesEnum.domainAuthority].label = `${this.domainAuthLabel}.${directionLabel}`;

        if (this.sortTypeMap[MonitoringSortTypesEnum.engagement]) {
            this.sortTypeMap[MonitoringSortTypesEnum.engagement].label = `${this.engagementLabel}.${directionLabel}`;
        }

        if (this.sortTypeMap[MonitoringSortTypesEnum.engagement_rate]) {
            this.sortTypeMap[MonitoringSortTypesEnum.engagement_rate].label = `${this.engagementRateLabel}.${directionLabel}`;
        }
    }
}

export class MonitoringSavedCoverageDisplaySettingsModel extends MonitoringDisplaySettingsModel {
    constructor(defaultDisplayPreferences: UserAppSavedCoverageDisplaySettingsModel) {
        super(defaultDisplayPreferences);
        delete this.sortTypeMap[MonitoringSortTypesEnum.relevancy];
        this.sortTypeOptions = Object.values(this.sortTypeMap);
    }

    public getCurrentSelections(): UserAppSavedCoverageDisplaySettingsModel {
        return {
            pageSize: this.selectedPageSize.value,
            sortDirection: this.selectedSortDirection.value,
            sortType: this.selectedSortType.value as SavedCoverageSortType,
            showImages: this.showImages,
            showMentionIndex: this.showMentionIndex,
            isCompactMentionDisplay: this.isCompactMentionDisplay,
            resultsViewMode: this.resultsViewMode
        };
    }

    protected setSortLabelsBasedOnSortDirection(sortDirection: SortOrderEnum) {
        const directionLabel = sortDirection === SortOrderEnum.ascending
            ? 'asc'
            : 'desc';

        this.sortTypeMap[MonitoringSortTypesEnum.published].label = `${this.publishedLabel}.${directionLabel}`;
        this.sortTypeMap[MonitoringSortTypesEnum.audience].label = `${this.audienceLabel}.${directionLabel}`;
        this.sortTypeMap[MonitoringSortTypesEnum.available].label = `${this.availableLabel}.${directionLabel}`;
        this.sortTypeMap[MonitoringSortTypesEnum.domainAuthority].label = `${this.domainAuthLabel}.${directionLabel}`;

        if (this.sortTypeMap[MonitoringSortTypesEnum.engagement]) {
            this.sortTypeMap[MonitoringSortTypesEnum.engagement].label = `${this.engagementLabel}.${directionLabel}`;
        }

        if (this.sortTypeMap[MonitoringSortTypesEnum.engagement_rate]) {
            this.sortTypeMap[MonitoringSortTypesEnum.engagement_rate].label = `${this.engagementRateLabel}.${directionLabel}`;
        }
    }
}

export class MonitoringBriefingDisplaySettingsModel  {

    protected readonly pageSizesMap = {
        [PageSizeEnum.twentyFive]: new DropdownOptionModel('25', PageSizeEnum.twentyFive),
        [PageSizeEnum.fifty]: new DropdownOptionModel('50', PageSizeEnum.fifty),
        [PageSizeEnum.oneHundred]: new DropdownOptionModel('100', PageSizeEnum.oneHundred),
        [PageSizeEnum.twoHundred]: new DropdownOptionModel('200', PageSizeEnum.twoHundred)
    };

    public pageSizeOptions: DropdownOptionModel<PageSizeEnum>[] = [];
    public selectedPageSize: DropdownOptionModel<PageSizeEnum> = null;

    constructor(defaultDisplayPreferences) {
        this.selectedPageSize = this.pageSizesMap[defaultDisplayPreferences.pageSize];
        this.pageSizeOptions = Object.values(this.pageSizesMap);
    }

    public getCurrentSelections(): UserAppBriefingDisplaySettingsModel {
        return {
            pageSize: this.selectedPageSize.value,
        };
    }
}

export class MonitoringSubscriptionDisplaySettingsModel  {
    public resultsViewMode: MonitoringViewModesEnum;

    constructor(defaultDisplayPreferences) {
        this.resultsViewMode = defaultDisplayPreferences.resultsViewMode;
    }

    public getCurrentSelections(): UserAppSubscriptionDisplaySettingsModel {
        return {
            resultsViewMode: this.resultsViewMode
        };
    }
}

